import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { FieldSearchSelect } from "components/AtomsV2";

import { createChartXAxisLabel } from "utils/charts";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import { useGetEMSSetpointsQuery } from "state/communities/communitiesApiSlice";

import EmsChartCard from "./EmsChartCard";

const COMMUNITY_ID = "__community__";

const INJECT_TYPES = new Set(["INJECT", "STORAGE_DISCHARGE", "EV_DISCHARGE", "PRODUCTION"]);

function signedValue(sp) {
  const abs = Math.abs(sp.setpoint_value);
  if (abs < 0.0001) return 0;
  return INJECT_TYPES.has(sp.setpoint_type) ? -abs : abs;
}

function getSetpointKey(sp) {
  return sp.resource_id || (sp.meter_id ? `meter:${sp.meter_id}` : null);
}

/* Split setpoints into two stacked series per timestamp: consume/charge (the
   positive part, above the axis) and inject/discharge (negative, below). */
function buildSplit(rawData) {
  const byTs = new Map(); // ts -> { c, i }
  rawData.forEach((sp) => {
    const v = signedValue(sp);
    const e = byTs.get(sp.datetime_timestamp) || { c: 0, i: 0 };
    if (v > 0) e.c += v;
    else if (v < 0) e.i += v;
    byTs.set(sp.datetime_timestamp, e);
  });
  const sorted = [...byTs.entries()].sort(([a], [b]) => new Date(a) - new Date(b));
  if (sorted.length === 0) return null;
  return {
    labels: sorted.map(([ts]) => createChartXAxisLabel(ts, "MIN15")),
    consume: sorted.map(([, e]) => Number(e.c.toFixed(3))),
    inject: sorted.map(([, e]) => Number(e.i.toFixed(3))),
  };
}

function SetpointsResourceChart({ moduleType, startDatetime, endDatetime, onDataStatus }) {
  const { t } = useTranslation(["community"]);
  const community = useSelector(selectCurrentCommunity);
  const communityId = community?.id;

  const { data: setpointsData, isLoading } = useGetEMSSetpointsQuery(
    {
      communityId,
      moduleType,
      startDatetime,
      endDatetime,
      limit: 20000,
      offset: 0,
    },
    { skip: !communityId }
  );

  const rawData = useMemo(() => setpointsData?.data || [], [setpointsData]);
  const isEmpty = !isLoading && rawData.length === 0;

  useEffect(() => {
    if (!isLoading && onDataStatus) onDataStatus(!isEmpty);
  }, [isLoading, isEmpty, onDataStatus]);

  const resources = useMemo(() => {
    const map = new Map();
    rawData.forEach((sp) => {
      const key = getSetpointKey(sp);
      if (key && !map.has(key)) {
        map.set(key, sp.resource_name || sp.meter_name || key);
      }
    });
    return [...map.entries()].map(([id, name]) => ({ id, name }));
  }, [rawData]);

  const [selectedResourceId, setSelectedResourceId] = useState(null);
  const effectiveResourceId =
    selectedResourceId || (resources.length > 1 ? COMMUNITY_ID : resources[0]?.id);

  const chartData = useMemo(() => {
    if (rawData.length === 0) return null;
    if (effectiveResourceId === COMMUNITY_ID) return buildSplit(rawData);
    if (!effectiveResourceId) return null;
    return buildSplit(rawData.filter((sp) => getSetpointKey(sp) === effectiveResourceId));
  }, [rawData, effectiveResourceId]);

  if (isLoading || isEmpty) return null;

  const selectedResource = resources.find((r) => r.id === effectiveResourceId);
  const selectorLabel =
    effectiveResourceId === COMMUNITY_ID
      ? t("unified_ems.chart_community_aggregate")
      : selectedResource?.name ?? "";

  const legend = [
    { label: t("unified_ems.chart_consumption"), color: "var(--info)" },
    { label: t("unified_ems.chart_injection"), color: "var(--accent)" },
  ];

  const toolbar =
    resources.length > 1 ? (
      <FieldSearchSelect
        value={effectiveResourceId}
        onChange={(v) => setSelectedResourceId(v)}
        options={[
          { value: COMMUNITY_ID, label: t("unified_ems.chart_community_aggregate") },
          ...resources.map(({ id, name }) => ({ value: id, label: name })),
        ]}
      />
    ) : (
      <span className="text-[12px] text-ink-3">{selectorLabel}</span>
    );

  // Two stacked series: charge above the axis (blue), discharge below (teal).
  const series = [
    {
      name: t("unified_ems.chart_consumption"),
      data: chartData?.consume || [],
      color: "var(--info)",
    },
    {
      name: t("unified_ems.chart_injection"),
      data: chartData?.inject || [],
      color: "var(--accent)",
    },
  ];

  return (
    <EmsChartCard
      title={t("unified_ems.chart_title")}
      legend={legend}
      toolbar={toolbar}
      series={series}
      labels={chartData?.labels || []}
      unit="kW"
      type="bar"
      stacked
    />
  );
}

SetpointsResourceChart.defaultProps = {
  startDatetime: undefined,
  endDatetime: undefined,
  onDataStatus: undefined,
};

SetpointsResourceChart.propTypes = {
  moduleType: PropTypes.string.isRequired,
  startDatetime: PropTypes.string,
  endDatetime: PropTypes.string,
  onDataStatus: PropTypes.func,
};

export default SetpointsResourceChart;
